import { ref, watch, computed } from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import store from '@/store'
import router from '@/router/index'
import { resolvePaymentStatusVariant, resolvePaymentStatusLabel } from './paymentStatus'
import { resolveOrderStatusVariant, resolveOrderStatusLabel, purchaseOptionLabel } from './orderStatus'

function toArray(value) {
  if (value === undefined || value === null || value === '') return []
  return Array.isArray(value) ? value : [value]
}

export default function useSalesOrderList() {
  const toast = useToast()
  const route = router.history.current

  const refSalesOrdersTable = ref(null)

  const tableColumns = [
    {
      key: 'stringID',
      label: 'SO No.',
      sortable: true,
    },
    {
      key: 'needType',
      label: 'Need Type',
      sortable: true,
    },
    {
      key: 'customerName',
      label: 'Purchaser',
      sortable: true,
    },
    {
      key: 'agent',
      label: 'Agent',
      sortable: true,
    },
    {
      key: 'orderTotal',
      label: 'Total',
      sortable: false,
    },
    {
      key: 'amountPaid',
      label: 'Payable',
      sortable: true,
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
    },
    {
      key: 'paymentStatus',
      label: 'Payment Status',
      sortable: false,
    },
    {
      key: 'purchaseOption',
      label: 'Purchase Option',
      sortable: false,
    },
    {
      key: 'pendingDept',
      label: 'Pending Dept.',
      sortable: false,
    },
    {
      key: 'latestComment',
      label: 'Latest Comment',
      sortable: false,
    },
    {
      key: 'createdAt',
      label: 'Submitted On',
      sortable: true,
    },
    { key: 'actions' },
  ]
  const perPage = ref(route.query.perPage ?? process.env.VUE_APP_PER_PAGE_CONTENT)
  const totalSalesOrders = ref(0)
  const currentPage = ref(route.query.page ?? 1)
  const perPageOptions = [10, 25, 50, 100]
  const searchQuery = ref(route.query.search ?? '')
  const sortBy = ref(route.query.sortBy ?? 'stringID')
  const isSortDirDesc = ref(route.query.sortDesc ? (route.query.sortDesc !== 'false') : true)
  const createdAtFilter = ref(route.query.createdAt || '')
  const productTypeFilter = ref(toArray(route.query.productType))
  const needTypeFilter = ref(toArray(route.query.needType))
  const paymentStatusFilter = ref(toArray(route.query.paymentStatus))
  const rankFilter = ref(toArray(route.query.rank))
  const agencyFilter = ref(route.query.agency || '')
  const agentSelfPurchaseFilter = ref(route.query.agentSelfPurchase === 'true')
  const currentDataLength = ref(0)
  const itemListData = ref([])
  let initialLoad = true

  // Tab bar - the 4 exhaustive status buckets (Processing/Action Required/Processed/Voided) plus
  // the 2 cross-cutting department filters (Pending My Dept/Others) plus the unfiltered All Sales
  // Orders. Only one active at a time - status and deptFilter are mutually exclusive query params
  // (a status tab clears deptFilter and vice versa), same partition confirmed against the design.
  const TABS = ['Processing', 'Action Required', 'Processed', 'Voided', 'Pending My Dept', 'Pending Others', 'All Sales Orders']
  const currentTab = ref(TABS.includes(route.query.tab) ? route.query.tab : 'All Sales Orders')
  const statusCounts = ref({
    Processing: 0, 'Action Required': 0, Processed: 0, Voided: 0, 'Pending My Dept': 0, 'Pending Others': 0, 'All Sales Orders': 0,
  })
  const DEPT_TABS = { 'Pending My Dept': 'mine', 'Pending Others': 'others' }

  const fetchStatusCounts = () => {
    store.dispatch('app-sales-orders/fetchSalesOrderStatusCounts', {}, { root: true })
      .then(response => {
        statusCounts.value = response.data.counts
      })
      .catch(() => {})
  }

  const dataMeta = computed(() => {
    const localItemsCount = refSalesOrdersTable.value ? refSalesOrdersTable.value.localItems.length : 0
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalSalesOrders.value,
    }
  })

  const refetchData = () => {
    refSalesOrdersTable.value.refresh()
  }

  const fetchSalesOrders = () => {
    const isStatusTab = ['Processing', 'Action Required', 'Processed', 'Voided'].includes(currentTab.value)
    const deptFilter = DEPT_TABS[currentTab.value] || ''
    store
      .dispatch('app-sales-orders/fetchSalesOrders', {
        search: searchQuery.value,
        perPage: perPage.value,
        page: currentPage.value,
        sortBy: sortBy.value,
        sortDesc: isSortDirDesc.value,
        createdAt: createdAtFilter.value,
        productType: productTypeFilter.value,
        needType: needTypeFilter.value,
        paymentStatus: paymentStatusFilter.value,
        rank: rankFilter.value,
        agency: agencyFilter.value,
        agentSelfPurchase: agentSelfPurchaseFilter.value,
        status: isStatusTab ? currentTab.value : '',
        deptFilter,
      },
      { root: true })
      .then(response => {
        const { salesOrders, total } = response.data
        itemListData.value = salesOrders.map(order => ({
          ...order,
          latestComment: (order.comments && order.comments.length) ? order.comments[order.comments.length - 1].text : null,
        }))
        currentDataLength.value = salesOrders.length
        totalSalesOrders.value = total
        if (!initialLoad) {
          router.replace({
            query: {
              search: searchQuery.value,
              perPage: perPage.value,
              page: currentPage.value,
              sortBy: sortBy.value,
              sortDesc: isSortDirDesc.value,
              createdAt: createdAtFilter.value,
              productType: productTypeFilter.value,
              needType: needTypeFilter.value,
              paymentStatus: paymentStatusFilter.value,
              rank: rankFilter.value,
              agency: agencyFilter.value,
              agentSelfPurchase: agentSelfPurchaseFilter.value,
              tab: currentTab.value,
            },
          }).catch(() => {})
        }
        initialLoad = false
      })
      .catch(error => {
        toast({
          component: ToastificationContent,
          props: {
            title: error.data.message || process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  const selectTab = tab => {
    currentTab.value = tab
    currentPage.value = 1
    fetchSalesOrders()
  }

  let timer = null
  watch([createdAtFilter], () => {
    if (timer) {
      clearTimeout(timer)
    }
    timer = setTimeout(() => {
      currentPage.value = 1
      fetchSalesOrders()
    }, 1000)
  }, { deep: true })

  watch([searchQuery], (current, prev) => {
    const currentVal = current[0]
    const prevVal = prev[0]
    if (timer) {
      clearTimeout(timer)
    }
    timer = setTimeout(() => {
      if ((!currentVal && prevVal) || (currentVal && currentVal.length > 2)) {
        currentPage.value = 1
        fetchSalesOrders()
      }
    }, 1000)
  }, { deep: true })

  watch([sortBy, perPage, isSortDirDesc, currentPage], () => {
    fetchSalesOrders()
  }, { deep: true })

  // Product/Need Type/Payment Status/Rank/Agency/Agent Self-Purchase are applied explicitly via
  // the Filters sidebar's "Apply filter" button (not live on every change like search/date), so no
  // watcher here - the sidebar calls this directly once the user confirms their selection.
  const applyFilters = () => {
    currentPage.value = 1
    fetchSalesOrders()
  }

  fetchSalesOrders()
  fetchStatusCounts()

  return {
    fetchSalesOrders,
    tableColumns,
    perPage,
    currentPage,
    totalSalesOrders,
    dataMeta,
    perPageOptions,
    searchQuery,
    sortBy,
    isSortDirDesc,
    refSalesOrdersTable,

    resolvePaymentStatusVariant,
    resolvePaymentStatusLabel,
    resolveOrderStatusVariant,
    resolveOrderStatusLabel,
    purchaseOptionLabel,
    refetchData,

    currentDataLength,
    itemListData,

    createdAtFilter,
    productTypeFilter,
    needTypeFilter,
    paymentStatusFilter,
    rankFilter,
    agencyFilter,
    agentSelfPurchaseFilter,
    applyFilters,

    currentTab,
    statusCounts,
    selectTab,
  }
}
